import React, {
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
} from "react";
import Box from "@mui/material/Box";
import SearchIcon from "@mui/icons-material/Search";
import Divider from "@mui/material/Divider";
import { InputAdornment, OutlinedInput } from "@mui/material";
import { SearchInputComponentProps } from "./search-input-component.props";
import AlertComponent from "../alert-component/alert-component";
import "./search-input-component.scss";
import CloseIcon from "@mui/icons-material/Close";

/**
 * SearchInputComponent renders a text input with clear and search icons.
 * Calls onSearch with trimmed values and exposes a ref method to clear.
 */
const SearchInputComponent: React.FC<SearchInputComponentProps> = ({
  placeholder = "",
  isReset,
  onSearch,
  ref,
}) => {
  const [searchText, setSearchText] = useState<any>("");
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<
    "success" | "error" | "info" | "warning"
  >("success");
  useImperativeHandle(ref, () => ({
    clear: () => {
      setSearchText("");
    },
  }));

  useEffect(() => {
    setSearchText("");
  }, [isReset]);

  const handleSearch = useCallback(() => {
    if (searchText.trim() === "!") {
      setAlertMessage('Search cannot be performed with just "!"');
      setAlertType("info");
      setAlertOpen(true);
      return;
    }

    const value = searchText.trim();
    if (value) {
      onSearch(value);
    }
  }, [searchText, onSearch]);

  const handleSearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Enter" && searchText?.trim()) {
      handleSearch();
    }
  };
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearchText(value);
    onSearch(value);
  };

  const handleAlertClose = () => {
    setAlertOpen(false);
  };

  return (
    <Box>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        open={alertOpen}
        onClose={handleAlertClose}
      />
      <div className="form-search-input-component">
        <OutlinedInput
          id="search-leads"
          className="search-input"
          placeholder={placeholder}
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
          value={searchText}
          endAdornment={
            <InputAdornment position="end">
              {searchText && (
                <>
                  <CloseIcon
                    className="clear-icon"
                    onClick={() => {
                      setSearchText("");
                      onSearch("");
                    }}
                    style={{ cursor: "pointer", marginRight: 8 }}
                  />
                  <Divider
                    orientation="vertical"
                    flexItem
                    className="search-icon-divider"
                  />
                </>
              )}
              <SearchIcon
                onClick={handleSearch}
                className="search-icon"
                titleAccess="Click to Search"
                style={{ cursor: "pointer" }}
              />
            </InputAdornment>
          }
          fullWidth
          slotProps={{
            input: {
              style: {
                whiteSpace: "nowrap",
                overflow: "hidden !important",
                textOverflow: "ellipsis",
              },
            },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              height: "40px",
            },
            borderRadius: "8px",
            "& .MuiInputBase-input": {
              padding: "12px 14px",
            },
          }}
        />
      </div>
    </Box>
  );
};

export default SearchInputComponent;
